import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { ArrowUpRight, Baby, Briefcase, Building2, Code2, GraduationCap, Handshake, Palette, Rocket, ShieldCheck, Sparkles, Users } from "lucide-react";
import { AdultsArt, KidsArt, CompaniesArt } from "../components/LandingCardArt";

function useFounderPhoto() {
  const [photo, setPhoto] = useState("");
  useEffect(() => { api.get("/catalog/founder").then((r) => setPhoto(r.data.photo_url)).catch(() => {}); }, []);
  return photo;
}

function Avatar({ photo, className }) {
  return photo
    ? <img src={photo} alt="Sankalp Sidhanth" className={`${className} object-cover object-top`} />
    : <span className={`${className} inline-block bg-amber-500/20`} />;
}

function MeetSankalpBand({ photo }) {
  return (
    <section className="relative z-10 max-w-7xl mx-auto px-6 pb-14">
      <Link to="/meet-sankalp" data-testid="landing-meet-sankalp-band" className="group grid md:grid-cols-12 gap-6 items-center rounded-3xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-[#0d0d0f] to-violet-500/10 p-6 md:p-8 hover:border-amber-500/60 transition-smooth">
        <div className="md:col-span-2"><Avatar photo={photo} className="w-24 h-24 md:w-28 md:h-28 rounded-2xl border border-amber-500/40" /></div>
        <div className="md:col-span-7">
          <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono">The educator behind it all</div>
          <div className="mt-2 font-adult-heading text-3xl md:text-4xl font-semibold tracking-tight">Meet <span className="font-adult-serif italic text-amber-400">Sankalp</span></div>
          <p className="mt-2 text-sm md:text-base text-zinc-400 max-w-xl leading-relaxed">Book a private 1-on-1, join a live workshop, or invite him to your campus or workplace.</p>
        </div>
        <div className="md:col-span-3 md:justify-self-end">
          <span className="inline-flex items-center gap-2 rounded-full bg-amber-500 group-hover:bg-amber-400 text-black font-semibold px-6 py-3 text-sm glow-amber transition-smooth">Meet Sankalp <ArrowUpRight className="w-4 h-4" /></span>
        </div>
      </Link>
    </section>
  );
}

export default function Landing() {
  const photo = useFounderPhoto();
  return (
    <div className="min-h-screen bg-[#09090B] text-white relative overflow-hidden">
      {/* Brand strip */}
      <div className="relative z-20 max-w-7xl mx-auto px-6 pt-6 flex items-center justify-between">
        <BrandLogo testid="landing-brand-logo" />
        <div className="flex items-center gap-4">
          <Link to="/meet-sankalp" data-testid="landing-header-meet-sankalp" className="hidden sm:inline-flex items-center gap-2 rounded-full border border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 pl-1 pr-4 py-1 text-sm font-semibold transition-smooth">
            <Avatar photo={photo} className="w-7 h-7 rounded-full" />Meet Sankalp
          </Link>
          <Link to="/login" data-testid="landing-login-link" className="text-sm text-zinc-400 hover:text-white transition-smooth">Sign in →</Link>
        </div>
      </div>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-14 pb-8 text-center">
        <div className="text-[11px] uppercase tracking-[0.32em] text-amber-400 font-adult-mono mb-5">👋 Welcome — pick your door</div>
        <h1 className="font-adult-heading font-semibold tracking-tight text-4xl sm:text-5xl md:text-6xl leading-[1.05] max-w-3xl mx-auto">
          Learning for <span className="font-adult-serif italic text-amber-400">yourself</span>, your{" "}
          <span className="font-adult-serif italic text-[#FF6B6B]">child</span>, or your{" "}
          <span className="font-adult-serif italic text-violet-400">team</span>?
        </h1>
        <p className="mt-5 text-zinc-400 max-w-xl mx-auto text-lg leading-relaxed">
          Three very different worlds, one educator. Choose the entrance that fits.
        </p>
        <Link to="/meet-sankalp" data-testid="landing-meet-sankalp-cta" className="group mt-8 inline-flex items-center gap-3 rounded-full border border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/15 hover:border-amber-400 pl-1.5 pr-5 py-1.5 transition-smooth glow-amber">
          <Avatar photo={photo} className="w-10 h-10 rounded-full border border-amber-500/50" />
          <span className="text-left leading-tight">
            <span className="block font-adult-heading font-semibold text-white">Meet Sankalp</span>
            <span className="block text-[11px] text-amber-300/80 font-adult-mono">1-on-1s · workshops · campus talks</span>
          </span>
          <ArrowUpRight className="w-4 h-4 text-amber-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </section>

      {/* Three doors */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-5 pb-14">
        {/* ADULTS */}
        <Link
          to="/adults"
          data-testid="landing-adults-cta"
          className="group relative overflow-hidden rounded-3xl border border-zinc-800 bg-[#0d0d0f] transition-smooth hover:border-amber-500/60 hover:-translate-y-1"
        >
          <div className="relative bg-black grid-bg-adult">
            <div className="absolute inset-0 grain pointer-events-none opacity-70" />
            <div className="absolute top-4 right-4 rounded-full bg-black/60 backdrop-blur-md border border-amber-500/30 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-adult-mono text-amber-300 flex items-center gap-1.5 z-10">
              <ShieldCheck className="w-3 h-3" /> Adults
            </div>
            <div className="absolute inset-x-2 top-8 h-[46%] pointer-events-none" data-testid="landing-adults-art">
              <AdultsArt />
            </div>
            <div className="relative aspect-[4/5] flex items-end px-6 pb-6 pt-16">
              <div className="w-full">
                <div className="w-14 h-14 rounded-2xl bg-amber-500 flex items-center justify-center mb-4 glow-amber">
                  <Briefcase className="w-7 h-7 text-black" />
                </div>
                <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 px-3 py-1 mb-2.5 font-adult-mono text-[11px] uppercase tracking-[0.22em] font-bold">
                  Ages 18+ · Professionals
                </div>
                <div className="font-adult-heading text-4xl md:text-5xl font-bold tracking-tight leading-[0.95]">
                  For <span className="text-amber-400">Adults</span>
                </div>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed max-w-[17rem]">
                  Job-ready coding, analytics &amp; weekend bootcamps to grow your career.
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <ul className="space-y-2.5 mb-5">
              {[
                { icon: <GraduationCap className="w-4 h-4" />, text: "16-wk weekend bootcamp · Analytics" },
                { icon: <Users className="w-4 h-4" />, text: "Live cohorts · Sat + Sun · 9:30 IST" },
                { icon: <Code2 className="w-4 h-4" />, text: "Mini-courses · coming soon" },
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-zinc-300">
                  <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">{f.icon}</div>
                  <span className="leading-relaxed">{f.text}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between border-t border-zinc-800 pt-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-adult-mono">What's inside</div>
                <div className="font-adult-heading text-lg mt-0.5 text-amber-400">Weekend Bootcamp</div>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center transition-smooth group-hover:scale-110 group-hover:rotate-[-8deg] glow-amber">
                <ArrowUpRight className="w-5 h-5 text-black" />
              </div>
            </div>
            <div className="mt-3 text-center text-[10px] text-zinc-500 font-adult-mono uppercase tracking-[0.2em]">Enter the workspace →</div>
          </div>
        </Link>

        {/* KIDS */}
        <Link
          to="/kids"
          data-testid="landing-kids-cta"
          className="group relative overflow-hidden rounded-3xl bg-white transition-smooth hover:-translate-y-1 kids-shadow"
        >
          <div className="relative bg-gradient-to-br from-[#FF6B6B] via-[#FF8E8E] to-[#FFB4A2]">
            <div className="absolute top-6 right-6 w-28 h-28 rounded-full bg-[#FFE66D] kids-blob opacity-70" />
            <div className="absolute bottom-8 right-12 w-16 h-16 rounded-full bg-[#4ECDC4] kids-blob opacity-70" />
            <div className="absolute top-4 right-4 rounded-full bg-white/25 backdrop-blur-md border border-white/30 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-white flex items-center gap-1.5 z-10">
              <Palette className="w-3 h-3" /> Kids
            </div>
            <div className="absolute inset-x-2 top-8 h-[46%] pointer-events-none z-[5]" data-testid="landing-kids-art">
              <KidsArt />
            </div>
            <div className="relative aspect-[4/5] flex items-end px-6 pb-6 pt-16">
              <div className="w-full">
                <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center mb-4 shadow-lg">
                  <Baby className="w-7 h-7 text-[#FF6B6B]" />
                </div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/30 backdrop-blur-md border border-white/40 text-white px-3 py-1 mb-2.5 font-kids-heading text-[11px] uppercase tracking-[0.22em] font-bold">
                  Ages 5 – 16
                </div>
                <div className="font-kids-heading text-4xl md:text-5xl font-extrabold tracking-tight leading-[0.95] text-white">
                  For Kids
                </div>
                <p className="mt-2 text-sm text-white/90 font-medium leading-relaxed max-w-[17rem]">
                  Playful clubs in coding, photography &amp; aerospace for young explorers.
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 text-[#2B2D42]">
            <ul className="space-y-2.5 mb-5">
              {[
                { icon: <Code2 className="w-4 h-4" />, text: "Coding · Scratch, Python & mini-games", color: "#FF6B6B", bg: "rgba(255,107,107,0.1)" },
                { icon: <Sparkles className="w-4 h-4" />, text: "Photography · storytelling through the lens", color: "#4ECDC4", bg: "rgba(78,205,196,0.15)" },
                { icon: <Rocket className="w-4 h-4" />, text: "Aerospace · how planes fly & rockets soar", color: "#B08900", bg: "rgba(255,230,109,0.35)" },
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#2B2D42]">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: f.bg, color: f.color }}>{f.icon}</div>
                  <span className="leading-relaxed font-medium">{f.text}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between border-t border-[#F3E8E0] pt-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8D99AE] font-bold">What's inside</div>
                <div className="font-kids-heading text-lg mt-0.5 text-[#FF6B6B] font-bold">Coding · Photo · Aero</div>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#FF6B6B] flex items-center justify-center transition-smooth group-hover:scale-110 group-hover:rotate-[12deg] kids-shadow">
                <ArrowUpRight className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="mt-3 text-center text-[10px] text-[#FF6B6B] font-bold uppercase tracking-[0.2em]">Enter the playground →</div>
          </div>
        </Link>

        {/* COMPANIES */}
        <Link
          to="/companies"
          data-testid="landing-companies-cta"
          className="group relative overflow-hidden rounded-3xl border border-zinc-800 bg-[#0d0d0f] transition-smooth hover:border-violet-500/60 hover:-translate-y-1"
        >
          <div className="relative bg-gradient-to-br from-[#1a1030] via-[#0d0d0f] to-[#0a0a15]">
            <div className="absolute inset-0 grain pointer-events-none opacity-40" />
            <div className="absolute top-6 right-8 w-24 h-24 rounded-full bg-violet-500/20 blur-2xl" />
            <div className="absolute bottom-10 left-8 w-32 h-32 rounded-full bg-violet-500/10 blur-3xl" />
            <div className="absolute top-4 right-4 rounded-full bg-black/60 backdrop-blur-md border border-violet-500/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-adult-mono text-violet-300 flex items-center gap-1.5 z-10">
              <Building2 className="w-3 h-3" /> B2B
            </div>
            <div className="absolute inset-x-2 top-8 h-[46%] pointer-events-none" data-testid="landing-companies-art">
              <CompaniesArt />
            </div>
            <div className="relative aspect-[4/5] flex items-end px-6 pb-6 pt-16">
              <div className="w-full">
                <div className="w-14 h-14 rounded-2xl bg-violet-500 flex items-center justify-center mb-4 shadow-[0_0_24px_rgba(139,92,246,0.5)]">
                  <Handshake className="w-7 h-7 text-white" />
                </div>
                <div className="inline-flex items-center gap-2 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-300 px-3 py-1 mb-2.5 font-adult-mono text-[11px] uppercase tracking-[0.22em] font-bold">
                  B2B · For Teams
                </div>
                <div className="font-adult-heading text-4xl md:text-5xl font-bold tracking-tight leading-[0.95]">
                  For <span className="text-violet-400">Companies</span>
                </div>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed max-w-[17rem]">
                  Train your team &amp; hire our job-ready bootcamp graduates.
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <ul className="space-y-2.5 mb-5">
              {[
                { icon: <GraduationCap className="w-4 h-4" />, text: "Custom cohorts to upskill your team" },
                { icon: <Handshake className="w-4 h-4" />, text: "Hire job-ready bootcamp graduates" },
                { icon: <Sparkles className="w-4 h-4" />, text: "Signature: Prof. Swiss German course" },
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-zinc-300">
                  <div className="w-7 h-7 rounded-lg bg-violet-500/10 border border-violet-500/30 flex items-center justify-center text-violet-400 shrink-0">{f.icon}</div>
                  <span className="leading-relaxed">{f.text}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between border-t border-zinc-800 pt-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-adult-mono">Custom quote</div>
                <div className="font-adult-heading text-xl mt-0.5 text-violet-400 font-adult-mono">Let's talk</div>
              </div>
              <div className="w-12 h-12 rounded-full bg-violet-500 flex items-center justify-center transition-smooth group-hover:scale-110 group-hover:rotate-[-8deg] shadow-[0_0_24px_rgba(139,92,246,0.5)]">
                <ArrowUpRight className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="mt-3 text-center text-[10px] text-zinc-500 font-adult-mono uppercase tracking-[0.2em]">Book a discovery call →</div>
          </div>
        </Link>
      </section>

      <MeetSankalpBand photo={photo} />

      {/* Trust strip */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-4 pb-16">
        {[
          { k: "2,500+", v: "students taught" },
          { k: "85%+", v: "placement outcomes" },
          { k: "16-wk", v: "weekend bootcamp" },
          { k: "5–16", v: "kids age range" },
        ].map((s) => (
          <div key={s.v} className="rounded-2xl border border-zinc-800 p-5 text-center md:text-left">
            <div className="font-adult-heading text-2xl md:text-3xl font-semibold tracking-tight">{s.k}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-adult-mono mt-1">{s.v}</div>
          </div>
        ))}
      </section>

      <div className="relative z-10 pb-14 max-w-7xl mx-auto px-6 flex items-center justify-between text-xs text-zinc-500 font-adult-mono">
        <span>© {new Date().getFullYear()} Code With Sankalp</span>
        <Link to="/meet-sankalp" data-testid="landing-founder-link" className="hover:text-amber-400 transition-smooth">Meet Sankalp →</Link>
      </div>
    </div>
  );
}
